import { useEffect, useState } from 'react';

import {
  ApolloError,
  useLazyQuery,
  WatchQueryFetchPolicy,
} from '@apollo/client';

import { LaneType } from '../__generated__/staticTypes';
import { getClient } from '../apollo';
import getChannelForProfile from '../graphql/query/getChannelForProfile';
import { isProbablyUUID } from 'uuid-encoding';
import { AddressType } from '../types/AddressType';
import {
  ChannelLanguagesType,
  EnabledLanguagesForMembersType,
} from '../types/ChannelType';
import { ProfileType } from '../types/ProfileType';
import { ActiveChannelTypeEnum } from 'constants-channel';

type Params = {
  channelId?: string | null;
  channelSlug?: string | null;
  options?: {
    fetchPolicy?: WatchQueryFetchPolicy;
  };
  includeExternalRef?: boolean;
};

export type ChannelProfileType = {
  _id: LaneType.UUID;
  _created: string;
  _updated: string;
  name: string;
  description: string;
  slug: string;
  type: ActiveChannelTypeEnum;
  isPublic: boolean;
  isDiscoverable: boolean;
  isLive: boolean;
  isSub: boolean;
  inviteOnly: boolean;
  address: AddressType;
  profile: ProfileType;
  settings: {
    hasBillingPaymentsEnabled: boolean;
    hasVisitorManagementEnabled: boolean;
    hasWorkOrderServiceRequestsEnabled: boolean;
    hasWorkOrderPreventiveMaintenanceEnabled: boolean;
    hasWorkOrderEquipmentEnabled: boolean;
    multiLanguageEnabled?: boolean;
    channelLanguages?: ChannelLanguagesType;
    enabledLanguagesForMembers?: EnabledLanguagesForMembersType;
    hasMessagingAndMarketplaceEnabled: boolean;
    hasWorkOrderCrossPropertyEnabled: boolean;
    hasInspectionsEnabled: boolean;
  };
};

export default function useChannelProfileQuery({
  channelId,
  channelSlug,
  options,
}: Params): {
  channel: ChannelProfileType | null;
  loading: boolean;
  error: ApolloError | undefined;
} {
  const [channel, setChannel] = useState<ChannelProfileType | null>(null);
  const [fetchChannel, { data, loading, error, called }] = useLazyQuery(
    getChannelForProfile,
    {
      client: getClient(),
      fetchPolicy: options?.fetchPolicy,
    }
  );

  useEffect(() => {
    if (channelId || channelSlug) {
      // may be a slug or a id passed in
      const variables = {
        id: channelId,
        slug: channelSlug,
      };

      if (!isProbablyUUID(channelId)) {
        // do the ol switcheroo, this is actually a slug.
        delete variables.id;
        variables.slug = channelId;
      }

      fetchChannel({
        variables,
      });
    }
  }, [channelId, channelSlug]);

  useEffect(() => {
    // Defer update if there is no data or we are re-fetching or it has been called
    if (!data || loading || !called) return;

    setChannel(data?.channel);
  }, [data, loading, called]);

  return {
    channel,
    loading,
    error,
  } as const;
}
